import React from 'react';
import axios from 'axios';
import { Button, Form, Header, Label, Message, Segment, Table } from 'semantic-ui-react';
import { fetchFaculties } from '../../Lookups/util';

const pair = p => `${p.sourceId}:${p.studyProgramId}`;
const planStatuses = {
    active: { text: 'Aktivan NPP', color: 'green' },
    pasive: { text: 'Pasivan NPP', color: 'orange' },
    archived: { text: 'Arhiviran NPP', color: 'grey' }
};

export default class CourseSplit extends React.Component {
    state = {
        sourceIds: [],
        courses: [],
        orgUnits: [],
        orgUnitId: '',
        loadingOrgUnits: true,
        targets: [],
        assignments: {},
        inspected: null,
        preview: null,
        tested: false,
        busy: false,
        busyAction: null,
        searching: false,
        error: '',
        retainHistoricalSurveys: true,
        confirm: false,
        result: null,
        showDetails: false
    };

    componentDidMount() {
        fetchFaculties().then(({ data }) => {
            if (!this.unmounted) {
                this.setState({
                    orgUnits: data.map(ou => ({ key: ou.id, value: ou.id, text: ou.name })), loadingOrgUnits: false
                });
            }
        }).catch(error => {
            if (!this.unmounted) this.setState({ loadingOrgUnits: false, error: this.errorText(error) });
        });
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.searchSequence = (this.searchSequence || 0) + 1;
    }

    selectOrgUnit = async (event, { value }) => {
        this.searchSequence = (this.searchSequence || 0) + 1;
        const sequence = this.searchSequence;
        this.update({
            orgUnitId: value,
            sourceIds: [],
            courses: [],
            targets: [],
            assignments: {},
            inspected: null,
            searching: !!value,
            confirm: false
        });
        if (!value) return;
        try {
            // Use the same active-course listing as Administrator > Predmeti.
            const { data } = await axios.get(`course/basic/${value}/false`);
            if (!this.unmounted && sequence === this.searchSequence) {
                this.setState({ courses: data.filter((c, index) => data.findIndex(other => other.id === c.id) === index), searching: false });
            }
        } catch (error) {
            if (!this.unmounted && sequence === this.searchSequence) this.setState({ searching: false, error: this.errorText(error) });
        }
    };

    errorText = error => {
        const data = error.response && error.response.data;
        if (typeof data === 'string' && data.trim() && !data.trim().startsWith('<')) return data;
        return data && typeof data.message === 'string' ? data.message :
            'Zahtjev nije završen. Provjerite vezu s backendom i zapis greške. Ne ponavljajte spremanje bez provjere rezultata.';
    };

    update = change => this.setState({
        ...change, preview: null, tested: false, result: null, error: '', confirm: false
    });

    payload = () => ({
        sourceIds: this.state.sourceIds,
        targets: this.state.targets,
        assignments: this.state.inspected.plans.map(p => ({
            sourceId: p.sourceId, studyProgramId: p.studyProgramId, targetKey: this.state.assignments[pair(p)]
        })),
        retainHistoricalSurveys: this.state.retainHistoricalSurveys,
        previewToken: this.state.preview && this.state.preview.previewToken
    });

    run = async action => {
        if (this.state.busy) return;
        if (action === 'execute' && (!this.state.tested || !this.state.preview || !this.state.preview.previewToken)) return;
        this.setState({
            busy: true, busyAction: action, error: '', confirm: action === 'execute'
        });
        try {
            const { data } = await axios.post(
                `course-split/${action}`,
                action === 'inspect' ? { sourceIds: this.state.sourceIds } : this.payload()
            );
            if (this.unmounted) return;
            if (action === 'inspect') {
                const assignments = {};
                data.plans.forEach(p => { assignments[pair(p)] = `existing-${p.sourceId}`; });
                this.setState({
                    inspected: data,
                    assignments,
                    preview: null,
                    tested: false,
                    result: null,
                    targets: data.courses.map(c => ({ key: `existing-${c.id}`, existingId: c.id, comment: c.comment || '' }))
                });
            } else if (action === 'preview') {
                this.setState({ preview: data, tested: false, result: null });
            } else if (action === 'simulate') {
                this.setState({ tested: true, result: data });
            } else {
                this.setState({
                    result: data, inspected: null, preview: null, tested: false
                });
            }
        } catch (error) {
            if (!this.unmounted) {
                this.setState({
                    error: this.errorText(error),
                    tested: false,
                    ...(action !== 'inspect' ? { preview: null } : {})
                });
            }
        } finally {
            if (!this.unmounted) this.setState({ busy: false, busyAction: null, confirm: false });
        }
    };

    targetLabel = target => {
        const source = this.state.courses.find(c => c.id === (target.existingId || target.templateId));
        return `${target.existingId ? 'Postojeći predmet' : 'Novi predmet'} — ${source ? source.name : ''} — ${target.comment || 'unesite komentar'}`;
    };

    editTarget = (key, change) => this.update({
        targets: this.state.targets.map(t => t.key === key ? { ...t, ...change } : t)
    });

    render() {
        const {
            sourceIds, courses, targets, assignments, inspected, preview, tested, busy, busyAction, searching,
            error, retainHistoricalSurveys, confirm, result, orgUnits, orgUnitId, loadingOrgUnits
        } = this.state;
        const options = courses.map(c => ({
            key: c.id,
            value: c.id,
            text: c.name,
            content: <div>{c.name}<div style={{ color: '#888', fontSize: '0.9em' }}>{c.comment || 'Bez komentara'}</div></div>
        }));
        const targetOptions = targets.map(t => ({ key: t.key, value: t.key, text: this.targetLabel(t) }));
        const blockers = preview ? preview.blockers : inspected ? inspected.blockers : [];
        const sortedPlans = inspected ? [...inspected.plans].sort((a, b) =>
            (a.program_name || '').localeCompare(b.program_name || '', 'bs', { sensitivity: 'base' }) ||
            (a.plan_name || '').localeCompare(b.plan_name || '', 'bs', { sensitivity: 'base' })) : [];
        return (<div>
            <Header as="h1">Razdvajanje predmeta</Header>
            {error && <Message negative content={error} />}
            {result && <Message positive>
                <Message.Header>{result.simulation ? 'Test je prošao — promjene nisu spremljene' : 'Razdvajanje je spremljeno'}</Message.Header>
                <p>{result.simulation ? 'Svi poslovni podaci su vraćeni. Test može ostaviti praznine u numeraciji ID-jeva.' : `Oznaka izvršavanja: ${result.runId}`}</p>
                {!result.simulation && <p>{Object.entries(result.targetIds).map(([key, id]) => {
                    const target = targets.find(t => t.key === key);
                    return `${target ? target.comment : key}: #${id}`;
                }).join(' | ')}</p>}
                {(result.warnings || []).map(w => <p key={w}>{w}</p>)}
            </Message>}
            <Segment.Group raised>
                <Segment raised><Header as="h3">Odabir predmeta</Header></Segment>
                <Segment clearing>
                    <Message>
                        <p>Ova funkcionalnost služi za razdvajanje zajedničkog predmeta na više predmeta ili za ispravljanje raspodjele više postojećih predmeta po nastavnim planovima i programima (NPP).
                            Za svaki NPP odaberite predmet kojem će pripadati nakon razdvajanja, a sistem prema tome usklađuje postojeće studentske evidencije.</p>
                        <Button
                            type="button"
                            basic
                            color="blue"
                            size="small"
                            icon={this.state.showDetails ? 'chevron up' : 'chevron down'}
                            labelPosition="left"
                            content={this.state.showDetails ? 'Sažmi objašnjenje' : 'Prikaži detaljno objašnjenje'}
                            aria-expanded={this.state.showDetails}
                            aria-controls="course-split-details"
                            onClick={() => this.setState(state => ({ showDetails: !state.showDetails }))}
                        />
                        {this.state.showDetails && <div id="course-split-details" style={{ marginTop: 14 }}>
                            <ol>
                                <li><strong>Odabir i pregled.</strong> Nakon odabira organizacione jedinice možete izabrati od jednog do deset njenih aktivnih predmeta.
                                    Prikazuju se svi NPP-ovi povezani s odabranim predmetima, uključujući one na kojima postoje historijski studentski upisi iako predmet više nije dodan na plan.
                                    Broj u koloni „Studentski upisi“ predstavlja evidencije upisa na implementacije predmeta, a ne nužno broj različitih osoba.</li>
                                <li><strong>Predmeti nakon razdvajanja.</strong> Za svaki prikazani NPP birate jedan od odabranih postojećih predmeta ili novi predmet.
                                    Novom predmetu kopiraju se osnovni podaci, veze s organizacionim jedinicama, prevodi i konstante iz predmeta koji odaberete kao predložak, kada ti podaci postoje.
                                    Na odabrane predmete upisuju se komentari koje unesete u formi.</li>
                                <li><strong>Veze s NPP-ovima.</strong> Postojeća veza predmeta i NPP-a preusmjerava se na predmet odabran za taj NPP nakon razdvajanja, bez promjene ID-ja te veze.
                                    Time se čuvaju podaci i evidencije koji su vezani za taj ID, poput šifre predmeta na planu i pripadajućih priznavanja.
                                    Za historijske upise bez takve veze ne dodaje se automatski nova veza predmeta i NPP-a.</li>
                                <li><strong>Implementacije po semestrima.</strong> Studenti kojima ostaje izvorni predmet ostaju na izvornim implementacijama.
                                    Za svaki par izvorna implementacija i predmet odabran nakon razdvajanja koji ima upise za premještanje kreira se kopija implementacije, povezana s istim semestrom odnosno semestrima.
                                    Kopiraju se nastavnici, grupe, jezici, skale ocjenjivanja i ostale podržane prateće veze.
                                    Implementacije se ne spajaju automatski s postojećim implementacijama odabranog predmeta samo zato što pripadaju istoj akademskoj godini.</li>
                                <li><strong>Studentski upisi.</strong> Upisi studenata prevezuju se na odgovarajuću implementaciju predmeta koji ste odabrali za njihov NPP nakon razdvajanja.
                                    Obuhvaćeni su postojeći upisi iz svih akademskih godina, ne samo tekuće godine.
                                    Ne kreira se novi upis studenta u semestar; postojeći ID-jevi studentskih upisa i njihove veze s upisanim semestrima ostaju sačuvani.</li>
                                <li><strong>Ispiti, prijave, ocjene i grupe.</strong> Za kopirane implementacije kopiraju se ispitne aktivnosti s datumima i statusima, rokovi prijava te podržane veze nastavnika, komisija i grupa.
                                    Postojeće studentske prijave i ispitne evidencije prevezuju se na odgovarajuće kopirane ispite i nastavnike.
                                    Usklađuju se veze nastavnika uz konačne ocjene i pripadnost studenata grupama.
                                    Vrijednosti ocjena i ostali poslovni podaci se ne preračunavaju; studentske evidencije i ocjene zadržavaju svoje ID-jeve.</li>
                                <li><strong>Šta ostaje na izvoru.</strong> Izvorni predmeti, implementacije i ispiti se ne brišu, čak ni kada nakon premještanja ostanu bez studenata.
                                    Implementacije bez upisa koje treba premjestiti ne kopiraju se.
                                    Historijske ankete, odgovori i zbirni podaci ostaju na izvornim implementacijama kao historija zajedničkog predmeta i ne razdvajaju se po NPP-ovima.</li>
                                <li><strong>Kada sistem zaustavlja razdvajanje i zašto?</strong> Sistem prije spremanja provjerava može li prenijeti evidencije bez nejasnoća i gubitka podataka.
                                    Na primjer, ako student na istom upisu na studijski program već ima upise na oba odabrana predmeta, a oba želite prebaciti na isti predmet nakon razdvajanja,
                                    sistem neće sam odlučiti koji upis ili koju ocjenu zadržati — postupak će zaustaviti.
                                    Također, ako su oba trenutna predmeta dodana na isti NPP i za oba odaberete isti predmet nakon razdvajanja, njihove veze s planom mogu imati različite šifre ili priznavanja,
                                    pa ih sistem neće sam spojiti ili obrisati.
                                    Spremanje se zaustavlja i kada je, primjerice, ocjena povezana s ispitom drugog studentskog upisa ili grupa ne pripada odgovarajućoj implementaciji.
                                    Ako postoje dodatne povezane evidencije ili automatske radnje baze koje ovaj postupak ne podržava, potrebna je tehnička provjera prije nastavka.
                                    Poruka će navesti pronađenu prepreku. Raspodjelu treba ispraviti ili evidencije provjeriti s tehničkom podrškom; sistem ne bira niti briše sporne podatke umjesto vas.</li>
                                <li><strong>Pregled i test bez spremanja.</strong> „Provjeri raspodjelu“ prikazuje obim promjena i eventualne prepreke.
                                    „Testiraj bez spremanja“ izvršava postupak i provjere, pa poništava promjene podataka u bazi.
                                    Test može ostaviti praznine u numeraciji ID-jeva; ID-jevi iz testa nisu konačni ID-jevi.
                                    Pregled vrijedi 15 minuta i vezan je za korisnika. Promjena raspodjele ili obuhvaćenih podataka zahtijeva novi pregled i test.</li>
                                <li><strong>Trajno spremanje i evidencija.</strong> Tek nakon uspješnog testa i potvrde „Da, spremi“ promjene se potvrđuju u jednoj transakciji.
                                    Prije završetka provjerava se očuvanje obuhvaćenih studentskih podataka; ako provjera ne prođe, promjene se poništavaju.
                                    U bazi se uz uspješno spremanje bilježi i tehnički zapis o izvršenom razdvajanju, u tabeli „course_split_app_audit“ — to je dnevnik izvršavanja.
                                    U njemu piše koji je korisnik pokrenuo postupak, kada ga je izvršio, koju je raspodjelu odabrao, koji su novi zapisi nastali i s kojim su izvornim zapisima povezani.
                                    Čuvaju se i podaci obuhvaćenih studentskih evidencija, veza s NPP-ovima i osnovnih predmeta prije izmjene, radi naknadne provjere.
                                    Oznaka izvršavanja prikazana nakon spremanja služi tehničkoj podršci da pronađe upravo taj zapis.
                                    Dnevnik nije dodatni izvještaj na frontu, ne šalje obavijesti i ne omogućava automatsko poništavanje već spremljenog razdvajanja.
                                    Nije kopija cijele baze i ne zamjenjuje backup. Zapis nastao tokom probnog testa poništava se zajedno s testnim promjenama.
                                    Nakon spremanja osvježavaju se privremeno zapamćeni podaci (cache) ove backend instance.</li>
                            </ol>
                            <p><strong>Važno:</strong> Postupak radi nad bazom konfigurisanom na backendu — dev bazom u dev okruženju, odnosno produkcijskom bazom u produkcijskom okruženju.
                                Prije produkcijskog izvršavanja osigurajte rezervnu kopiju i termin bez paralelnih izmjena obuhvaćenih podataka.
                                Ako više backend instanci koristi istu bazu, potrebno je osvježiti i njihov cache.
                                Ako se veza prekine tokom spremanja, prvo provjerite rezultat i dnevnik izvršavanja prije ponavljanja postupka.</p>
                        </div>}
                    </Message>
                    <Form>
                        <Form.Group widths="equal">
                            <Form.Dropdown
                                label="Organizaciona jedinica"
                                fluid
                                selection
                                search
                                clearable
                                options={orgUnits}
                                value={orgUnitId}
                                placeholder="Odaberite organizacionu jedinicu"
                                loading={loadingOrgUnits}
                                disabled={busy || loadingOrgUnits}
                                onChange={this.selectOrgUnit}
                            />
                            <Form.Dropdown
                                label="Aktivni predmeti"
                                fluid
                                multiple
                                selection
                                search
                                placeholder={orgUnitId ? 'Odaberite predmete' : 'Prvo odaberite organizacionu jedinicu'}
                                options={options}
                                value={sourceIds}
                                loading={searching}
                                disabled={busy || searching || !orgUnitId}
                                onChange={(e, { value }) => this.update({
                                    sourceIds: value, inspected: null, targets: [], assignments: {}
                                })}
                            />
                        </Form.Group>
                        <Button
                            type="button"
                            color="blue"
                            basic
                            size="small"
                            icon="list"
                            labelPosition="left"
                            floated="right"
                            disabled={busy || !sourceIds.length || sourceIds.length > 10}
                            loading={busyAction === 'inspect'}
                            onClick={() => this.run('inspect')}
                            content="Prikaži planove i upise"
                        />
                    </Form>
                </Segment>
            </Segment.Group>
            {inspected && <React.Fragment>
                <Segment.Group raised>
                    <Segment raised><Header as="h3">Predmeti nakon razdvajanja</Header></Segment>
                    <Segment clearing>
                        <Form>
                            {targets.map(t => (<Form.Group key={t.key} widths="equal">
                                {t.existingId && <Form.Input label="Postojeći predmet" readOnly value={courses.find(c => c.id === t.existingId).name} />}
                                {!t.existingId &&
                                <Form.Dropdown
                                    label="Novi predmet — kopirati osnovne podatke iz"
                                    selection
                                    value={t.templateId}
                                    options={options.filter(o => sourceIds.includes(o.value))}
                                    disabled={busy}
                                    onChange={(e, { value }) => this.editTarget(t.key, { templateId: value })}
                                />}
                                <Form.Input
                                    label="Komentar predmeta"
                                    maxLength={100}
                                    value={t.comment}
                                    disabled={busy}
                                    onChange={(e, { value }) => this.editTarget(t.key, { comment: value })}
                                />
                                {!t.existingId && <Form.Field width={2}><div>Novi predmet</div>
                                    <Button
                                        type="button"
                                        circular
                                        basic
                                        color="red"
                                        size="small"
                                        icon="trash"
                                        title="Ukloni novi predmet"
                                        disabled={busy}
                                        onClick={() => this.update({
                                            targets: targets.filter(x => x.key !== t.key),
                                            assignments: Object.keys(assignments).reduce((a, key) => ({ ...a, [key]: assignments[key] === t.key ? '' : assignments[key] }), {})
                                        })}
                                    />
                                </Form.Field>}
                            </Form.Group>))}
                            <Button
                                type="button"
                                color="blue"
                                basic
                                size="small"
                                labelPosition="left"
                                icon="plus"
                                content="Dodaj novi predmet"
                                disabled={busy || targets.length >= 20}
                                onClick={() => this.update({ targets: [...targets, { key: `new-${Date.now()}`, templateId: sourceIds[0], comment: '' }] })}
                            />
                        </Form>
                    </Segment>
                </Segment.Group>
                <Segment.Group raised>
                    <Segment raised><Header as="h3">Raspodjela po planovima</Header></Segment>
                    <Segment clearing>
                        <div style={{ overflowX: 'auto' }}><Table celled selectable compact style={{ tableLayout: 'fixed', minWidth: 950, overflowWrap: 'break-word' }}>
                            <colgroup>
                                <col style={{ width: '20%' }} />
                                <col style={{ width: '42%' }} />
                                <col style={{ width: '8%' }} />
                                <col style={{ width: '30%' }} />
                            </colgroup>
                            <Table.Header><Table.Row>
                                <Table.HeaderCell>Trenutni predmet</Table.HeaderCell><Table.HeaderCell>Studijski program</Table.HeaderCell>
                                <Table.HeaderCell textAlign="center">Studentski upisi</Table.HeaderCell><Table.HeaderCell>Predmet nakon razdvajanja</Table.HeaderCell>
                            </Table.Row></Table.Header>
                            <Table.Body>{sortedPlans.map(p => {
                                const course = inspected.courses.find(c => c.id === p.sourceId);
                                const status = planStatuses[p.plan_status_key] || { text: p.plan_status_name || 'Status nije naveden', color: 'grey' };
                                return (<Table.Row key={pair(p)}>
                                    <Table.Cell>{course.name}<div style={{ color: '#888' }}>{course.comment}</div></Table.Cell>
                                    <Table.Cell>{p.program_name}{p.plan_name && <React.Fragment> – <em>{p.plan_name}</em></React.Fragment>}
                                        {p.program_comment && <div style={{ color: '#888', fontSize: '0.9em' }}>{p.program_comment}</div>}
                                        <div style={{ marginTop: 5 }}><Label basic size="mini" color={status.color}>{status.text}</Label></div>
                                        {!p.curriculumRows && <small style={{ color: '#a56700' }}>Historijski upisi — predmet više nije na planu</small>}
                                    </Table.Cell>
                                    <Table.Cell textAlign="center">{p.students}</Table.Cell>
                                    <Table.Cell><Form.Dropdown
                                        fluid
                                        selection
                                        options={targetOptions}
                                        placeholder="Odaberite predmet nakon razdvajanja"
                                        value={assignments[pair(p)] || ''}
                                        disabled={busy}
                                        onChange={(e, { value }) => this.update({ assignments: { ...assignments, [pair(p)]: value } })}
                                    /></Table.Cell>
                                </Table.Row>);
                            })}</Table.Body>
                        </Table></div>
                        <Form><Form.Checkbox
                            label="Historijske ankete ostaju na izvornim implementacijama"
                            checked={retainHistoricalSurveys}
                            disabled={busy}
                            onChange={(e, { checked }) => this.update({ retainHistoricalSurveys: checked })}
                        /></Form>
                        <Button
                            color="blue"
                            basic
                            size="small"
                            icon="check"
                            labelPosition="left"
                            floated="right"
                            disabled={busy || !inspected.plans.length}
                            loading={busyAction === 'preview'}
                            onClick={() => this.run('preview')}
                            style={{ marginTop: 15 }}
                            content="Provjeri raspodjelu"
                        />
                    </Segment>
                </Segment.Group>
                {blockers.length > 0 && <Message negative header="Spremanje je blokirano" list={blockers} />}
                {preview && <Segment.Group raised>
                    <Segment raised><Header as="h3">Pregled prije spremanja</Header></Segment>
                    <Segment clearing>
                        <p>Studentskih upisa za premještanje: <strong>{preview.studentsToMove}</strong> | Evidencija ispita/ocjena: <strong>{preview.activitiesToMove}</strong> | Novih implementacija: <strong>{preview.implementationsToCreate}</strong></p>
                        {preview.warnings.map(w => <Message warning key={w} content={w} />)}
                        <div style={{
                            display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8
                        }}
                        >
                            {confirm ? <React.Fragment>
                                <span style={{ alignSelf: 'center', marginRight: 8 }}>Jeste li sigurni da želite spremiti razdvajanje prema ovoj raspodjeli?</span>
                                <Button color="blue" basic size="small" disabled={busy} onClick={() => this.setState({ confirm: false })} content="Odustani" />
                                <Button
                                    color="blue"
                                    size="small"
                                    icon="save"
                                    labelPosition="left"
                                    loading={busyAction === 'execute'}
                                    disabled={busy || !tested || !preview.previewToken}
                                    onClick={() => this.run('execute')}
                                    content="Da, spremi"
                                />
                            </React.Fragment> : <React.Fragment>
                                <Button color="blue" basic size="small" icon="check circle outline" labelPosition="left" loading={busyAction === 'simulate'} disabled={busy || !preview.previewToken} onClick={() => this.run('simulate')} content="Testiraj bez spremanja" />
                                <Button
                                    color="blue"
                                    size="small"
                                    icon="save"
                                    labelPosition="left"
                                    disabled={busy || !tested || !preview.previewToken}
                                    onClick={() => this.setState({ confirm: true })}
                                    content="Spremi razdvajanje"
                                />
                            </React.Fragment>}
                        </div>
                    </Segment>
                </Segment.Group>}
            </React.Fragment>}
        </div>);
    }
}



// WEBPACK FOOTER //
// ./src/views/Admin/CourseSplit/CourseSplit.js